import { computed, inject, Injectable, signal } from '@angular/core';
import { finalize, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { TableLoadingModel } from '../../models/table-column.model';
import { AModel } from '../../models/resources/a-model';
import { BreadcrumbService } from '../utils/breadcrumb.service';
import { PaginationMeta } from '../../models/pagination-meta.model';
import { PaginationModel } from '../../models/pagination.model';
import { PaginationLinks } from '../../models/pagination-links.model';
import { RequestOptions } from '../../models/request-options.model';
import { HttpErrorsService } from '../utils/http-errors.service';
import { ModelService } from '../utils/model.service';
import { UrlParamModel } from '../../models/url-param.model';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export abstract class AResourceService<M extends AModel> {
  // currently fetched models, from index routes, probably paginated
  apiResponse = signal<{ data: M[]; filters?: UrlParamModel[] }>({data: []});
  paginationMeta = signal<PaginationMeta | null>(null);
  paginationLinks = signal<PaginationLinks | null>(null);

  models = computed(() => this.apiResponse().data);

  loading = signal<TableLoadingModel>({loading: false});

  // concrete fetched models that are needed for detail pages or breadcrumbs
  usedConcreteModels = signal<M[]>([]);
  currentConcreteModel = signal<M | null>(null);

  resourceUrl = signal<string | null>(null)

  protected standardGetAllUrlParams = signal<UrlParamModel[]>([])

  protected identifier: string

  protected modelService: ModelService
  protected http: HttpClient
  protected datePipe: DatePipe
  protected breadcrumbService: BreadcrumbService
  protected httpErrorsService: HttpErrorsService

  private getAllSubscription: Subscription | null = null;
  private cancelRequest$ = new Subject<void>();

  removeModel(id: number) {
    // Update the concrete models
    this.usedConcreteModels.update((models) => {
      return models.filter((m) => m.id != id);
    });

    // Check if the model exists in the apiResponse.data array and update it if necessary
    if (this.models() && this.models().length > 0) {
      this.apiResponse.update((apiResp) => {
        const updatedData = apiResp.data.filter((m) => m.id != id);
        return {...apiResp, data: updatedData};
      });
    }
  }

  addConcreteModel(model: M) {
    this.usedConcreteModels.update((models) => {
      return [...models, model];
    });
    this.currentConcreteModel.set(model);
  }

  addModel(model: any) {
    this.apiResponse.update((apiResp) => {
      const updatedData = Array.isArray(model) ? [...apiResp.data].concat(model) : [...apiResp.data, model]
      return {...apiResp, data: updatedData};
    });
  }

  updateModel(model: M) {
    // Update the concrete models
    this.usedConcreteModels.update((models) => {
      return models.map((m) => (m.id == model.id ? model : m));
    });
    console.log(this.currentConcreteModel())
    this.currentConcreteModel.set(model);

    // Check if the model exists in the apiResponse.data array and update it if necessary
    if (this.models() && this.models().length > 0) {
      this.apiResponse.update((apiResp) => {
        const updatedData = apiResp.data.map((m) =>
          m.id === model.id ? model : m,
        );
        return {...apiResp, data: updatedData};
      });
    }
  }

  updateAllModels(models: M[]) {
    this.apiResponse.update((apiResp) => {
      return {...apiResp, data: models};
    })
  }

  protected constructor(
    resourceUrl: string,
    protected paginated: boolean,
    protected includeRelations: string[] = []
  ) {
    this.datePipe = inject(DatePipe)
    this.http = inject(HttpClient)
    this.breadcrumbService = inject(BreadcrumbService)
    this.httpErrorsService = inject(HttpErrorsService)
    this.modelService = inject(ModelService)
    this.resourceUrl.set(resourceUrl)
    this.identifier = resourceUrl.replace('/', '')
  }

  setResourceUrl(url: string) {
    let oldUrl = this.resourceUrl()

    this.resourceUrl.set(url)

    if (oldUrl !== null && oldUrl != url)
      this.getAll({resetData: true});
  }

  getAll(options?: RequestOptions, resourceUrl: string | null = null): void {
    const preventPagination: boolean = options?.paginated !== undefined && !options.paginated

    // Cancel previous request if it exists
    if (this.getAllSubscription) {
      this.cancelRequest$.next(); // Signal to cancel the request
    }

    if (options?.resetData) {
      this.apiResponse.set({data: [], filters: this.apiResponse().filters})
      this.paginationMeta.set(null)
    }

    this.loading.set({
      loading: true,
      hasFilters: options?.urlParams != undefined && options?.urlParams.length > 0,
      lazyLoad: options?.lazyLoad,
      isPaginationChange:
        options?.pagination?.next || options?.pagination?.previous,
    });

    let urlParams: UrlParamModel[] = [
      ...this.getDefaultListParams(),
      ...(options?.urlParams ?? [])
    ];

    if (this.paginated && !preventPagination) {
      let page = 1;
      if (options?.resetPagination) this.paginationMeta.set(null);
      else page = this.paginationMeta()?.current_page ?? 1;

      if (options?.pagination?.next == true) page++;
      else if (options?.pagination?.previous == true && page > 1) page--;

      urlParams.push({key: 'page', value: `${page}`})
      urlParams.push({key: 'paginated', value: 'true'})
    } else {
      urlParams.push({key: 'paginated', value: 'false'})
    }

    const request$ = this.http.get<any>(
      environment.apiUrl + (resourceUrl ?? this.resourceUrl()) + this.buildQueryParams([...urlParams, ...this.standardGetAllUrlParams()]),
    );

    this.getAllSubscription = request$
      .pipe(
        takeUntil(this.cancelRequest$), // Cancel request when triggered
        finalize(() => this.loading.set({loading: false})),
      )
      .subscribe({
        next: (response: any) => {
          let data = (this.paginated && !preventPagination)
            ? (response as PaginationModel).data
            : (response as M[])
          let meta = (this.paginated && !preventPagination) ? (response as PaginationModel).meta : null;
          let links = (this.paginated && !preventPagination)
            ? (response as PaginationModel).links
            : null;
          this.apiResponse.set({
            data: options?.addToList ? [...this.apiResponse().data, ...data] : data,
            filters: options?.urlParams
          });
          this.paginationMeta.set(meta);
          this.paginationLinks.set(links);
        },
        error: (e) => {
          this.httpErrorsService.handleError(e);
        },
      });
  }

  getOne(id: number): Observable<M> {
    this.currentConcreteModel.set(null);
    return this.http
      .get<M>(environment.apiUrl + this.resourceUrl() + `/${id}` + this.buildQueryParams())
      .pipe(
        tap({
          next: (m: M) => {
            if (m) this.addConcreteModel(m);
          },
        }),
      );
  }

  update(id: number,
         data: any,
         resourceUrl: string | null = null,
         params: UrlParamModel[] = [],
         updateCurrentConcreteModel: boolean = true): Observable<M> {
    return this.http
      .put<M>(environment.apiUrl + (resourceUrl ?? this.resourceUrl()) + `/${id}` + this.buildQueryParams(params), data)
      .pipe(
        tap({
          next: (m: M) => {
            this.updateModel(m);
            if(updateCurrentConcreteModel)
              this.breadcrumbService.updateCurrentConcreteModel(
                this.modelService.getModelLabel(m, this.identifier)
              );
          },
        }),
      );
  }

  delete(id: number): Observable<any> {
    return this.http
      .delete<any>(environment.apiUrl + this.resourceUrl() + `/${id}`)
      .pipe(
        tap({
          next: () => {
            this.removeModel(id);
          },
        }),
      );
  }

  create(data: any,
         headers?: HttpHeaders,
         resourceUrl: string | null = null,
         params: UrlParamModel[] = [],
         isDownload: boolean = false): Observable<M> {
    let options: any = {headers: headers, responseType: undefined, observe: undefined}
    if(isDownload) {
      options.responseType = 'blob'
      options.observe = 'response'
    }
    return this.http
      .post<M>(environment.apiUrl + (resourceUrl ?? this.resourceUrl()) + this.buildQueryParams(params), data, options)
      .pipe(
        tap({
          next: (m: any) => {
            this.addModel(m)
          },
        }),
      );
  }

  getAllInstant(includes: string = ""): Observable<M[]> {
    return this.http.get<M[]>(
      environment.apiUrl + this.resourceUrl() + this.buildQueryParams([{key: 'paginated', value: 'false'}], includes)
    );
  }

  generatePdf(body: any, resourceUrl: string|undefined = undefined, params: UrlParamModel[] = []): Observable<HttpResponse<Blob>> {
    if(resourceUrl)
      this.resourceUrl.set(resourceUrl)
    return this.http.post(environment.apiUrl + this.resourceUrl() + '/generate-pdf' + this.buildQueryParams(params),
      body,
      {
        responseType: 'blob',
        observe: 'response'
      }
    ).pipe(
      tap((response: any) => {
        // Extract the filename from the Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'document';

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1];
          }
        }

        // Use the response body for the Blob
        const blob = new Blob([response.body], {type: response.body.type});
        const url = window.URL.createObjectURL(blob);

        // Create a link element for download
        const a = document.createElement('a');
        a.href = url;
        a.download = filename; // Use the filename from the header
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
      })
    )
  }

  protected buildQueryParams(existingParams: UrlParamModel[] = [], includes: string = ''): string {
    let params = existingParams;

    // Combine passed includes with existing ones from `this.includeRelations`
    const includeList: string[] = [];

    let paramIncludes = params.filter(p => p.key == 'include');
    params = params.filter(p => p.key != 'include');
    paramIncludes.forEach(p => {
      includeList.push( ...p.value.split(',').map(i => i.trim()) )
    })

    if (this.includeRelations.length > 0) {
      includeList.push(...this.includeRelations);
    }

    if (includes) {
      includeList.push(...includes.split(',').map(i => i.trim()));
    }

    if (includeList.length > 0) {
      params.push({key: 'include', value: [...new Set(includeList)].join(',')})
    }

    if(params.length === 0)
      return ''

    let paramsString = '?'
    params.forEach((param, i) => {
      if(i > 0)
        paramsString += '&'
      paramsString += `${param.key}=${param.isDate ? this.datePipe.transform(param.value, environment.serverDateFormat) : param.value}`
    })
    return paramsString
  }
  protected getDefaultListParams(): UrlParamModel[] {
    return [];
  }
}
